import { useState, useEffect } from 'react';
import { Sparkles, ArrowLeft, Home, LogOut, Zap, FileText, X, Settings, Calendar } from 'lucide-react';
import { ArticlesList } from './components/features/ArticlesList';
import { ArticleViewer } from './components/features/ArticleViewer';
import { Stepper, Step } from './components/ui/Stepper';
import { Button } from './components/ui/Button';

import { Loading } from './components/ui/Loading';
import { Login } from './components/features/Login';
import { logout as firebaseLogout } from './config/firebase';
import { AdminPanel } from './components/features/AdminPanel';
import { ProjectDashboard } from './components/features/ProjectDashboard';
import { BriefingSelector } from './components/features/BriefingSelector';
import { ChatBriefing } from './components/features/ChatBriefing';
import { TraditionalForm } from './components/features/TraditionalForm';
import { ProjectSelectorHeader } from './components/features/ProjectSelectorHeader';
import { ProjectSettings, TopicSuggestions } from './components/features/ProjectSettings';
import { Modal } from './components/ui/Modal';
import { BriefGenerator } from './components/features/BriefGenerator';
import { ResearchPanel } from './components/features/ResearchPanel';
import { ResearchResults } from './components/features/ResearchResults';
import { ArticleGenerator } from './components/features/ArticleGenerator';
import { ContentScheduler } from './components/features/ContentScheduler';
import { UsageStats } from './components/ui/UsageStats';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { projectService } from './services/api';
import { AnimatedGradientBackground } from './components/ui/AnimatedGradientBackground';
import type { Brief, ResearchResult, Article, Project, ArticleTypeId } from './types';
import { useDataCache } from './context/DataCacheContext';

const steps: Step[] = [
  { id: 'briefing', label: 'Briefing', description: 'Chat conversacional' },
  { id: 'structure', label: 'Estructura', description: 'Brief SEO' },
  { id: 'research', label: 'Investigación', description: 'Perplexity' },
  { id: 'results', label: 'Resultados', description: 'Revisar info' },
  { id: 'generation', label: 'Generación', description: 'Crear artículo' },
  { id: 'complete', label: 'Completado', description: 'Listo' },
];

function MainApp() {
  // DataCache context para sincronizar cambios
  const { refreshProjects } = useDataCache();

  // Estado de autenticación
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [, setCurrentUser] = useState<any>(null);

  const [showDashboard, setShowDashboard] = useState(true);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  // Initialize sessionId from storage if available
  const [sessionId, setSessionId] = useState<string | null>(() => sessionStorage.getItem('current_session_id'));
  const [briefingMode, setBriefingMode] = useState<'chat' | 'form' | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [activeView, setActiveView] = useState<'workflow' | 'settings' | 'topics'>('workflow');
  const [showScheduler, setShowScheduler] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Modal State
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  // Datos del flujo
  const [briefingData, setBriefingData] = useState<any>(null);
  const [brief, setBrief] = useState<Brief | null>(null);
  const [researchResults, setResearchResults] = useState<ResearchResult[]>([]);
  const [, setArticle] = useState<Article | null>(null);

  // Estado para tipo de artículo
  const [articleType, setArticleType] = useState<ArticleTypeId>('wikipedia');
  const [comparisonUrls, setComparisonUrls] = useState<string[]>([]);
  const [mainSourceUrl, setMainSourceUrl] = useState<string>('');

  const [showArticlesList, setShowArticlesList] = useState(false);
  const [viewingArticle, setViewingArticle] = useState<any>(null);

  // State Cleaning: When switching projects
  useEffect(() => {
    if (viewingArticle?.id) {
      // If we open a different article, we might want to clear or keep?
      // User requirement: "if I close and come back to SAME article, keep it".
      // So we ONLY clear if we switch PROJECTS or explicitly clear.
      // For now, simpler approach: we keep state.
    }
  }, [viewingArticle]);


  // Estado del tema visual (from Context)
  const { theme } = useTheme();


  const VersionBadge = () => (
    <div className="fixed bottom-2 right-2 px-3 py-1 bg-black/80 text-white text-xs rounded-full backdrop-blur-md z-[1000] font-mono border border-white/20 shadow-lg pointer-events-none">
      v3.0.1 (Latest)
    </div>
  );

  // Generar ID único de sesión
  const generateSessionId = () => {
    const newId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('current_session_id', newId);
    return newId;
  };

  // Effect to persist sessionId changes (redundant but safe)
  useEffect(() => {
    if (sessionId) {
      sessionStorage.setItem('current_session_id', sessionId);
    } else {
      sessionStorage.removeItem('current_session_id');
    }
  }, [sessionId]);

  // Verificar autenticación al cargar
  useEffect(() => {
    const verifyAuth = async () => {
      const token = sessionStorage.getItem('auth_token');
      const user = sessionStorage.getItem('user');

      if (!token || !user) {
        setAuthLoading(false);
        return;
      }

      try {
        const response = await fetch('/api/firebase-auth/verify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token })
        });

        if (response.ok) {
          const data = await response.json();
          setCurrentUser(data.user);
          setIsAuthenticated(true);
        } else {
          sessionStorage.removeItem('auth_token');
          sessionStorage.removeItem('user');
        }
      } catch (err) {
        // If API fails, use cached user data
        if (user) {
          try {
            const parsedUser = JSON.parse(user);
            setCurrentUser(parsedUser);
            setIsAuthenticated(true);
          } catch (parseErr) {
            sessionStorage.removeItem('auth_token');
            sessionStorage.removeItem('user');
          }
        } else {
          sessionStorage.removeItem('auth_token');
          sessionStorage.removeItem('user');
        }
      } finally {
        setAuthLoading(false);
      }
    };

    verifyAuth();
  }, []);



  const handleNavigation = (action: () => void) => {
    if (isProcessing) {
      setPendingAction(() => action);
      setShowConfirmModal(true);
    } else {
      action();
    }
  };

  const confirmNavigation = () => {
    if (pendingAction) {
      setIsProcessing(false);
      pendingAction();
      setPendingAction(null);
    }
    setShowConfirmModal(false);
  };

  const cancelNavigation = () => {
    setPendingAction(null);
    setShowConfirmModal(false);
  };

  // --- Handlers ---

  const handleBriefingComplete = async (data: any) => {
    setBriefingData(data);

    if (currentProject?.id) {
      try {
        await projectService.saveBriefingData(currentProject.id, data);
        await projectService.updateProject(currentProject.id, { status: 'in_progress' });
      } catch (err) {
        // Error saving briefing data
      }
    }
    setCurrentStepIndex(1);
  };

  const handleBriefComplete = async (generatedBrief: Brief) => {
    setBrief(generatedBrief);

    if (currentProject?.id) {
      try {
        await projectService.saveBrief(currentProject.id, generatedBrief);
        await projectService.updateProject(currentProject.id, {
          targetKeyword: generatedBrief.mainKeyword
        });
      } catch (err) {
        // Error saving brief
      }
    }

    setCurrentStepIndex(2);
  };

  const handleResearchComplete = async (results: ResearchResult[]) => {
    setResearchResults(results);

    if (currentProject?.id) {
      try {
        await projectService.saveResearch(currentProject.id, results);
      } catch (err) {
        // Error saving research data
      }
    }

    setCurrentStepIndex(3);
  };

  const handleResultsReviewed = () => {
    setCurrentStepIndex(4);
  };

  const handleArticleComplete = async (generatedArticle: Article) => {
    setArticle(generatedArticle);

    if (currentProject?.id) {
      try {
        await projectService.saveArticle(currentProject.id, generatedArticle);
        await projectService.updateProject(currentProject.id, {
          wordCount: generatedArticle.wordCount,
          status: 'completed'
        });
      } catch (err) {
        // Error saving article
      }
    }
  };

  const handleSelectProject = async (project: Project) => {
    try {

      if (!project.id) {
        throw new Error('El proyecto no tiene un ID válido');
      }

      const newSessionId = generateSessionId();
      const { sessionService } = await import('./services/api');
      await sessionService.createSession(
        newSessionId,
        project.id,
        project.briefingMode || null
      );

      setCurrentProject(project);
      setSessionId(newSessionId);
      setShowDashboard(false);

      // Reset logic
      setBriefingData(null);
      setBrief(null);
      setResearchResults([]);
      setArticle(null);
      setCurrentStepIndex(0);
      setBriefingMode(null);
      setShowArticlesList(false);
      setViewingArticle(null);
      // Reset article type state
      setArticleType('wikipedia');
      setComparisonUrls([]);
      setMainSourceUrl('');

    } catch (err) {
      // Error selecting project
    }
  };

  const handleLoginSuccess = (user: any) => {
    setIsAuthenticated(true);
    setCurrentUser(user);
    setIsAdmin(user.isAdmin || false);
    setAuthLoading(false);
  };

  const handleLogout = async () => {
    handleNavigation(async () => {
      try {
        await firebaseLogout();
      } catch (e) {
        // Error during logout
      }
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('user');
      setIsAuthenticated(false);
      setCurrentUser(null);
      setShowDashboard(true);
      setCurrentProject(null);
      setSessionId(null);
      setBriefingMode(null);
      setShowArticlesList(false); // Reset article list visibility
      setViewingArticle(null); // Reset article viewer
    });
  };

  const resetToDashboard = () => {
    setShowDashboard(true);
    setCurrentProject(null);
    setSessionId(null);
    setBriefingMode(null);
    setCurrentStepIndex(0);
    setBriefingData(null);
    setBrief(null);
    setResearchResults([]);
    setArticle(null);
    setShowArticlesList(false);
    setViewingArticle(null);
    // Reset article type state
    setArticleType('wikipedia');
    setComparisonUrls([]);
    setMainSourceUrl('');
  };

  const handleGlobalBack = () => {
    handleNavigation(() => {
      // Logic for "Smart Back":
      // 1. If we are in the workflow and not on the first step, go back one step.
      if (activeView === 'workflow' && currentStepIndex > 0) {
        setCurrentStepIndex(prev => prev - 1);
        return;
      }

      // 2. If we are in Step 0 (Briefing/Selector) but inside a mode (chat/form), go back to mode selector.
      if (activeView === 'workflow' && currentStepIndex === 0 && briefingMode !== null) {
        setBriefingMode(null);
        return;
      }

      // 3. Otherwise default to Dashboard
      resetToDashboard();
    });
  };

  const handleModeSelect = async (mode: 'chat' | 'form') => {
    setBriefingMode(mode);
    setCurrentStepIndex(0);
    if (currentProject?.id) {
      try {
        await projectService.updateProject(currentProject.id, { briefingMode: mode });
      } catch (err) {
        // Error updating mode
      }
    }
  };

  // Handlers for article management
  const handleViewArticles = () => {
    setShowArticlesList(true);
  };

  const handleCloseArticles = () => {
    setShowArticlesList(false);
    setViewingArticle(null); // Ensure viewer is closed if list is closed
  };

  const handleSelectArticle = (article: any) => {
    setViewingArticle(article);
  };

  const handleCloseArticleViewer = () => {
    setViewingArticle(null);
    // Optional: Clear image generation state if we want to reset on close
    // But user might want persistence. For now we keep it.
  };

  const handleArticleUpdate = (updatedArticle: any) => {
    setViewingArticle(updatedArticle);
  };

  const handleBackToArticles = () => {
    setViewingArticle(null);
    setShowArticlesList(true);
  };

  const handleUpdateProject = async (data: Partial<Project>) => {
    if (!currentProject) return;
    try {
      await projectService.updateProject(currentProject.id, data);
      setCurrentProject(prev => prev ? { ...prev, ...data } : null);

      // CRITICAL: Refrescar el cache para que ContentScheduler vea los cambios (ej: taxonomías)
      await refreshProjects();

      alert('✅ Proyecto actualizado correctamente');
    } catch (error) {
      alert('Error al actualizar el proyecto');
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    try {
      await projectService.deleteProject(projectId);
      resetToDashboard();
    } catch (error) {
      alert('Error al eliminar el proyecto');
    }
  };

  // --- Render Helpers ---



  const getHeaderClasses = () => {
    if (theme === 'light') return 'bg-white border-b border-gray-200 text-gray-900 shadow-sm';
    return 'bg-black/20 border-b border-white/10 text-white backdrop-blur-md shadow-sm';
  };

  const getButtonTextClass = () => {
    if (theme === 'light') return 'text-gray-600 hover:text-gray-900 hover:bg-gray-100';
    return 'text-white hover:bg-white/10';
  };



  const renderStepContent = () => {
    if (!currentProject) {
      return <p>Error: No project selected.</p>;
    }

    // Step 0: Briefing Selector OR Content
    if (currentStepIndex === 0) {
      if (briefingMode === null) {
        return (
          <BriefingSelector
            onSelectMode={handleModeSelect}
            onOpenScheduler={() => setShowScheduler(true)}
            projectId={currentProject?.id}
            articleType={articleType}
            onArticleTypeChange={setArticleType}
            comparisonUrls={comparisonUrls}
            onComparisonUrlsChange={setComparisonUrls}
            mainSourceUrl={mainSourceUrl}
            onMainSourceUrlChange={setMainSourceUrl}
          />
        );
      }
      return briefingMode === 'chat' ? (
        <ChatBriefing
          onComplete={handleBriefingComplete}
          sessionId={sessionId || undefined}
        />
      ) : (
        <TraditionalForm
          onComplete={handleBriefingComplete}
          projectId={currentProject?.id}
          sessionId={sessionId || undefined}
          articleType={articleType}
        />
      );
    }

    switch (currentStepIndex) {
      case 1:
        return (
          <BriefGenerator
            initialData={briefingData}
            onComplete={handleBriefComplete}
            sessionId={sessionId || undefined}
            articleType={articleType}
          />
        );
      case 2:
        return (
          <ResearchPanel
            brief={brief!}
            onComplete={handleResearchComplete}
            setIsProcessing={setIsProcessing}
            onBack={() => handleNavigation(() => setCurrentStepIndex(1))}
            sessionId={sessionId || undefined}
            articleType={articleType}
          />
        );
      case 3:
        return (
          <ResearchResults
            results={researchResults}
            onContinue={handleResultsReviewed}
            mainTopic={brief?.mainKeyword || brief?.title || 'Tema del proyecto'}
            onBack={() => handleNavigation(() => setCurrentStepIndex(2))}
          />
        );
      case 4:
        return (
          <ArticleGenerator
            brief={brief!}
            researchResults={researchResults}
            onComplete={handleArticleComplete}
            projectId={currentProject.id}
            setIsProcessing={setIsProcessing}
            styleAnalysis={briefingData?.styleAnalysis}
            articleType={articleType}
            comparisonUrls={comparisonUrls}
            mainSourceUrl={mainSourceUrl}
            ctaText={briefingData?.ctaText}
            ctaUrl={briefingData?.ctaUrl}
            isTitleStrict={briefingData?.isTitleStrict}
          />
        );
      case 5:
        return (
          <div className="flex flex-col items-center justify-center h-full p-4">
            <Sparkles className="h-24 w-24 text-primary mb-6 animate-pulse" />
            <h2 className="text-4xl font-bold mb-4 text-center">¡Artículo Generado!</h2>
            <p className="text-lg text-center text-muted-foreground mb-8">
              Tu artículo está listo. Puedes revisarlo en el paso anterior.
            </p>
            <Button onClick={resetToDashboard} size="lg">
              <Home className="mr-2 h-5 w-5" /> Volver al Dashboard
            </Button>
          </div>
        );
      default:
        return <p>Paso desconocido</p>;
    }
  };

  // --- Views ---

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loading />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 w-screen h-screen overflow-hidden bg-background">
        <AnimatedGradientBackground variant={theme}>
          <div className="w-full h-full flex items-center justify-center p-4 overflow-y-auto">
            <div className="w-full max-w-md relative z-10">
              <Login onLoginSuccess={handleLoginSuccess} />
            </div>
          </div>
        </AnimatedGradientBackground>
        <VersionBadge />
      </div>
    );
  }

  if (showScheduler) {
    return (
      <div className="h-screen text-foreground flex flex-col relative w-full overflow-hidden">
        <AnimatedGradientBackground variant={theme}>
          <div className="relative z-10 w-full h-full flex flex-col">
            <header className={`flex justify-between items-center p-4 ${getHeaderClasses()}`}>
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  onClick={() => setShowScheduler(false)}
                  className={`${getButtonTextClass()} mr-4`}
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Volver
                </Button>
                <h1 className="text-2xl font-bold flex items-center">
                  <Calendar className="mr-2" />
                  Programador de Contenidos
                </h1>
              </div>
              <div className="flex items-center gap-4">
                <UsageStats theme={theme} />
              </div>
            </header>
            <main className="flex-grow p-6 w-full container mx-auto overflow-y-auto">
              <ContentScheduler initialProjectId={currentProject?.id} initialArticleType={articleType} />
            </main>
            <VersionBadge />
          </div>
        </AnimatedGradientBackground>
      </div>
    );
  }

  if (showDashboard) {
    return (
      <div className="h-screen text-foreground flex flex-col relative w-full overflow-hidden">
        <AnimatedGradientBackground variant={theme}>
          <div className="relative z-10 w-full h-full flex flex-col">
            <header className={`flex justify-between items-center p-4 ${getHeaderClasses()}`}>
              <h1 className="text-2xl font-bold flex items-center">
                <Zap className="mr-2" />
                AI Content Generator
              </h1>
              <div className="flex items-center gap-4">
                <UsageStats theme={theme} />
                {isAdmin && (
                  <Button
                    onClick={() => setShowAdminPanel(true)}
                    variant="ghost"
                    className={getButtonTextClass()}
                  >
                    <Settings className="mr-2 h-5 w-5" /> Admin
                  </Button>
                )}
                <Button onClick={handleLogout} variant="ghost" className={getButtonTextClass()}>
                  <LogOut className="mr-2 h-5 w-5" /> Cerrar Sesión
                </Button>
              </div>
            </header>
            <main className="flex-grow p-6 w-full container mx-auto">
              <ProjectDashboard
                onSelectProject={handleSelectProject}
                onOpenScheduler={() => {
                  setShowScheduler(true);
                }}
              />
            </main>
            <VersionBadge />
          </div>
        </AnimatedGradientBackground>
        {showAdminPanel && <AdminPanel onClose={() => setShowAdminPanel(false)} />}
      </div>
    );
  }

  return (
    <div className="h-screen text-foreground flex flex-col relative w-full overflow-hidden">
      <AnimatedGradientBackground variant={theme}>
        <div className="relative z-10 w-full h-full flex flex-col">
          <header className={`flex justify-between items-center p-4 relative z-50 ${getHeaderClasses()}`}>
            <div className="flex items-center">
              <ProjectSelectorHeader
                currentProject={currentProject}
                onSelectProject={(p) => handleNavigation(() => handleSelectProject(p))}
                theme={theme}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => handleNavigation(resetToDashboard)}
                variant="ghost"
                className={`${getButtonTextClass()} border border-transparent hover:border-current`}
                title="Ir al listado de proyectos"
              >
                <Home className="h-5 w-5" />
                <span className="sr-only sm:not-sr-only sm:ml-2">Proyectos</span>
              </Button>
              <div className={`h-6 w-px mx-2 ${theme === 'light' ? 'bg-gray-300' : 'bg-white/20'}`}></div>
              <UsageStats theme={theme} />
              <div className={`h-6 w-px mx-2 ${theme === 'light' ? 'bg-gray-300' : 'bg-white/20'}`}></div>
              <Button
                onClick={handleViewArticles}
                variant="ghost"
                className={`${getButtonTextClass()} border border-transparent hover:border-current`}
              >
                <FileText className="mr-2 h-5 w-5" />
                Ver Artículos
              </Button>
              <div className={`h-6 w-px mx-2 ${theme === 'light' ? 'bg-gray-300' : 'bg-white/20'}`}></div>
              <Button onClick={handleLogout} variant="ghost" className={getButtonTextClass()}>
                <LogOut className="mr-2 h-5 w-5" /> Cerrar Sesión
              </Button>
            </div>
          </header>

          {/* Main Layout: Top Stepper + Content */}
          <main className="flex-grow flex flex-col w-full h-full container mx-auto">
            {/* View Switching Tabs */}
            <div className={`w-full px-6 pt-6 -mb-6 relative z-20`}>
              <div className="max-w-7xl mx-auto relative flex items-center justify-center">
                {/* Back Button positioned absolutely to the left */}
                <div className="absolute left-0">
                  <Button
                    variant="ghost"
                    onClick={handleGlobalBack}
                    className={`${getButtonTextClass()} flex items-center gap-2`}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span className="font-medium">Volver</span>
                  </Button>
                </div>

                {/* Centered Tabs */}
                <div className="bg-gray-100/80 backdrop-blur-md p-1 rounded-xl inline-flex">
                  <button
                    onClick={() => handleNavigation(() => setActiveView('workflow'))}
                    className={`flex items-center space-x-2 px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${activeView === 'workflow'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>Generación de Contenido</span>
                  </button>

                  <button
                    onClick={() => handleNavigation(() => setActiveView('settings'))}
                    className={`flex items-center space-x-2 px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${activeView === 'settings'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <Settings className="w-4 h-4" />
                    <span>Configuración</span>
                  </button>

                  <button
                    onClick={() => handleNavigation(() => setActiveView('topics'))}
                    className={`flex items-center space-x-2 px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${activeView === 'topics'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    <FileText className="w-4 h-4" />
                    <span>Sugerencia de Temas</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Top Stepper Area - Only show in workflow mode */}
            {activeView === 'workflow' && (
              <div className={`w-full p-6 pt-10 ${theme === 'light' ? 'border-b border-gray-200 bg-gray-50' : 'border-b border-white/10 bg-black/5 backdrop-blur-sm'}`}>
                <div className="max-w-4xl mx-auto">
                  <Stepper steps={steps} currentStep={currentStepIndex} />
                </div>
              </div>
            )}

            {/* Content Area */}
            <div className="w-full p-6 overflow-auto flex-grow">
              <div className="min-h-full h-auto max-w-5xl mx-auto pb-24">
                {activeView === 'workflow' && renderStepContent()}

                {activeView === 'settings' && (
                  <ProjectSettings
                    project={currentProject!}
                    onUpdate={handleUpdateProject}
                    onDelete={handleDeleteProject}
                  />
                )}

                {activeView === 'topics' && currentProject && (
                  <TopicSuggestions
                    projectId={currentProject.id}
                    project={currentProject}
                    onUpdate={async (data) => {
                      if (!currentProject) return;
                      try {
                        await projectService.updateProject(currentProject.id, data);
                        setCurrentProject(prev => prev ? { ...prev, ...data } : null);
                      } catch (error) {
                        // Error updating project URLs
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </main>
        </div>
      </AnimatedGradientBackground>

      {/* Articles List Modal */}
      {showArticlesList && !viewingArticle && currentProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
            <div className="p-4 border-b flex justify-between items-center bg-gray-50">
              <h2 className="text-xl font-bold flex items-center text-gray-800">
                <FileText className="mr-2 h-5 w-5 text-primary-600" />
                Artículos de {currentProject.name}
              </h2>
              <button
                onClick={handleCloseArticles}
                className="p-2 hover:bg-gray-200 rounded-full text-gray-500 transition-colors"
                title="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-auto p-0 bg-gray-50 h-full">
              <ArticlesList projectId={currentProject.id} onViewArticle={handleSelectArticle} />
            </div>
          </div>
        </div>
      )}

      {/* Article Viewer Modal */}
      {viewingArticle && (
        <ArticleViewer
          article={viewingArticle}
          onClose={handleCloseArticleViewer}
          onBack={handleBackToArticles}
          onArticleUpdate={handleArticleUpdate}
          projectId={currentProject?.id}
          wordpressConnected={currentProject?.wordpress_connected}
        />
      )}

      {/* Navigation Guard Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={cancelNavigation}
        title="Proceso en curso"
        variant="warning"
        footer={
          <>
            <Button
              onClick={cancelNavigation}
              variant="ghost"
              className="text-gray-600"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmNavigation}
              className="bg-yellow-500 hover:bg-yellow-600 text-white"
            >
              Salir y perder progreso
            </Button>
          </>
        }
      >
        <p className="text-gray-600">
          Hay una investigación o generación de contenido en curso.
          Si sales ahora, el proceso se interrumpirá y perderás los datos no guardados.
        </p>
        <p className="mt-2 font-medium text-gray-800">
          ¿Estás seguro de que quieres salir?
        </p>
      </Modal>

      <VersionBadge />
    </div>
  );
}

import { DataCacheProvider } from './context/DataCacheContext';

// ... (existing imports)

function App() {
  return (
    <ThemeProvider>
      <DataCacheProvider>
        <MainApp />
      </DataCacheProvider>
    </ThemeProvider>
  );
}

export default App;
